import { useEffect, useState } from 'react'
import { api, money, today, fmtDate, ApiError } from '../api'
import { Page } from '../App'
import { Card, Alert, Spinner, Empty, Tabs, Stat, Badge } from '../components/ui'

export default function Reports() {
  const [tab, setTab] = useState('aging')
  return (
    <Page title="Reports & GST" subtitle="What your CA needs, as a download">
      <Tabs
        tabs={[
          { key: 'aging', label: 'Ageing' },
          { key: 'revenue', label: 'Revenue' },
          { key: 'pnl', label: 'Profit & loss' },
          { key: 'itc', label: 'Input tax credit' },
          { key: 'tds', label: 'TDS' },
          { key: 'gstr1', label: 'GSTR-1' },
          { key: 'exports', label: 'Exports' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'aging' && <Aging />}
      {tab === 'revenue' && <Revenue />}
      {tab === 'pnl' && <Pnl />}
      {tab === 'itc' && <Itc />}
      {tab === 'tds' && <Tds />}
      {tab === 'gstr1' && <Gstr1 />}
      {tab === 'exports' && <Exports />}
    </Page>
  )
}

function Aging() {
  const [data, setData] = useState<{ rows: Record<string, string | number>[]; asOn: string } | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api
      .get<{ rows: Record<string, string | number>[]; asOn: string }>('/reports/aging')
      .then(setData)
      .catch((e) => setError(e.message))
  }, [])

  if (error) return <Alert kind="error">{error}</Alert>
  if (!data) return <Spinner />

  const cols = [
    { key: 'not_due', label: 'Not due' },
    { key: 'd0_30', label: '1–30' },
    { key: 'd31_60', label: '31–60' },
    { key: 'd61_90', label: '61–90' },
    { key: 'd90_plus', label: '90+' },
  ]

  const totals = cols.map((c) => data.rows.reduce((s, r) => s + Number(r[c.key] ?? 0), 0))
  const grand = data.rows.reduce((s, r) => s + Number(r.total ?? 0), 0)

  return (
    <Card title={`Ageing of receivables as on ${data.asOn}`} tight>
      {data.rows.length === 0 ? (
        <Empty title="Nothing outstanding" hint="Every invoice is settled." />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Client</th>
                {cols.map((c) => (
                  <th key={c.key} className="r">
                    {c.label} days
                  </th>
                ))}
                <th className="r">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={String(r.client_id)}>
                  <td>{String(r.display_name || r.legal_name)}</td>
                  {cols.map((c) => (
                    <td key={c.key} className="num">
                      {Number(r[c.key]) ? money(Number(r[c.key])) : <span className="muted">—</span>}
                    </td>
                  ))}
                  <td className="num">
                    <strong>{money(Number(r.total))}</strong>
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#fafbfc' }}>
                <td>
                  <strong>Total</strong>
                </td>
                {totals.map((t, i) => (
                  <td key={i} className="num">
                    <strong>{money(t)}</strong>
                  </td>
                ))}
                <td className="num">
                  <strong>{money(grand)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function Revenue() {
  const [data, setData] = useState<{
    fy: string
    byMonth: Record<string, string | number>[]
    byClient: Record<string, string | number>[]
    taxSummary: Record<string, string | number>[]
    tds: { tds: number; received: number }
  } | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api
      .get<never>('/reports/revenue')
      .then((d) => setData(d as never))
      .catch((e) => setError(e.message))
  }, [])

  if (error) return <Alert kind="error">{error}</Alert>
  if (!data) return <Spinner />

  return (
    <>
      <Card title={`Revenue by month · FY ${data.fy}`} tight>
        {data.byMonth.length === 0 ? (
          <Empty title="No invoices this financial year" />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Month</th>
                <th className="r">Invoices</th>
                <th className="r">Taxable value</th>
                <th className="r">GST</th>
                <th className="r">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.byMonth.map((m) => (
                <tr key={String(m.month)}>
                  <td>{String(m.month)}</td>
                  <td className="num">{Number(m.invoices)}</td>
                  <td className="num">{money(Number(m.taxable))}</td>
                  <td className="num">{money(Number(m.tax))}</td>
                  <td className="num">
                    <strong>{money(Number(m.total))}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div style={{ height: 14 }} />

      <div className="grid c2">
        <Card title="By client" tight>
          <table className="tbl">
            <tbody>
              {data.byClient.map((c, i) => (
                <tr key={i}>
                  <td>{String(c.display_name || c.legal_name)}</td>
                  <td className="num">{Number(c.invoices)}</td>
                  <td className="num">
                    <strong>{money(Number(c.taxable))}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="Tax collected by supply type" tight>
          <table className="tbl">
            <thead>
              <tr>
                <th>Treatment</th>
                <th className="r">Taxable</th>
                <th className="r">CGST</th>
                <th className="r">SGST</th>
                <th className="r">IGST</th>
              </tr>
            </thead>
            <tbody>
              {data.taxSummary.map((t, i) => (
                <tr key={i}>
                  <td className="small">{String(t.tax_treatment)}</td>
                  <td className="num">{money(Number(t.taxable))}</td>
                  <td className="num">{money(Number(t.cgst))}</td>
                  <td className="num">{money(Number(t.sgst))}</td>
                  <td className="num">{money(Number(t.igst))}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={5} className="small muted" style={{ paddingTop: 12 }}>
                  TDS deducted by clients this year: <strong>{money(data.tds.tds)}</strong> — reconcile
                  against Form 26AS.
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>
    </>
  )
}

function Gstr1() {
  const [month, setMonth] = useState(today().slice(0, 7))
  const [data, setData] = useState<Record<string, never> | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setData(null)
    api
      .get<Record<string, never>>(`/reports/gstr1?month=${month}`)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed'))
  }, [month])

  const download = () => {
    if (!data) return
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `GSTR1-${month}.json`
    a.click()
  }

  const sections = data
    ? ([
        ['B2B — registered recipients', (data as never as { b2b: unknown[] }).b2b],
        ['B2CL — unregistered, inter-state over ₹2.5L', (data as never as { b2cl: unknown[] }).b2cl],
        ['B2CS — unregistered, summarised', (data as never as { b2cs: unknown[] }).b2cs],
        ['EXP — exports', (data as never as { exp: unknown[] }).exp],
        ['CDNR — credit & debit notes', (data as never as { cdnr: unknown[] }).cdnr],
        ['HSN summary', (data as never as { hsn: unknown[] }).hsn],
        ['Documents issued', (data as never as { docs: unknown[] }).docs],
      ] as [string, unknown[]][])
    : []

  return (
    <>
      <Alert kind="error">{error}</Alert>
      <div className="inline" style={{ marginBottom: 14, maxWidth: 400 }}>
        <div>
          <label className="small muted">Return period</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <div style={{ flex: 'none' }}>
          <button className="btn primary" onClick={download} disabled={!data}>
            Download JSON
          </button>
        </div>
      </div>

      {!data ? (
        <Spinner />
      ) : (
        <div className="grid" style={{ gap: 14 }}>
          {sections.map(([label, rows]) => (
            <Card key={label} title={`${label} · ${rows?.length ?? 0} row${rows?.length === 1 ? '' : 's'}`} tight>
              {!rows || rows.length === 0 ? (
                <div className="empty small">Nothing in this section for {month}.</div>
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        {Object.keys(rows[0] as object).map((k) => (
                          <th key={k}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i}>
                          {Object.values(r as object).map((v, j) => (
                            <td key={j} className="small">
                              {String(v ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  )
}

function Exports() {
  const fy = Number(today().slice(0, 4)) - (Number(today().slice(5, 7)) < 4 ? 1 : 0)
  return (
    <Card title="Downloads">
      <p className="muted small" style={{ marginTop: 0 }}>
        Hand these to your accountant instead of a screenshot.
      </p>
      <div className="btn-row">
        <a className="btn primary" href={`/api/reports/register.csv?fy=${fy}`}>
          Invoice register (CSV) · FY {fy}-{String((fy + 1) % 100).padStart(2, '0')}
        </a>
        <a className="btn" href={`/api/reports/register.csv?fy=${fy - 1}`}>
          Previous year
        </a>
      </div>
      <div className="sep" />
      <p className="small muted">
        The register is the statutory sales book: every numbered document with its taxable value, tax
        split by head, payments and status. Amounts are in rupees, not paise.
      </p>
    </Card>
  )
}

// ------------------------------------------------------------- profit & loss

function Pnl() {
  const [d, setD] = useState<null | {
    fy: string
    byMonth: Array<{ month: string; revenue: number; expenses: number; profit: number }>
    byCategory: Array<{ category: string; taxable: number; total: number; count: number }>
    totals: { revenue: number; expenses: number; profit: number; margin: number }
    gst: { outputTax: number; itc: number; netPayable: number }
  }>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<never>('/reports/pnl').then((x) => setD(x as never)).catch((e) => setError(e.message))
  }, [])

  if (error) return <Alert kind="error">{error}</Alert>
  if (!d) return <Spinner />

  return (
    <>
      <div className="grid c4" style={{ marginBottom: 16 }}>
        <Stat label="Revenue" value={money(d.totals.revenue)} note="Taxable value" />
        <Stat label="Expenses" value={money(d.totals.expenses)} note="Taxable value" />
        <Stat
          label="Net profit"
          value={money(d.totals.profit)}
          note={`${(d.totals.margin * 100).toFixed(1)}% margin`}
          tone={d.totals.profit >= 0 ? 'ok' : 'danger'}
        />
        <Stat label="Net GST payable" value={money(d.gst.netPayable)} note={`${money(d.gst.outputTax)} out − ${money(d.gst.itc)} credit`} />
      </div>

      <Card title={`Month by month · FY ${d.fy}`} tight>
        {d.byMonth.length === 0 ? (
          <Empty title="Nothing recorded this financial year" />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Month</th>
                <th className="r">Revenue</th>
                <th className="r">Expenses</th>
                <th className="r">Profit</th>
              </tr>
            </thead>
            <tbody>
              {d.byMonth.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td className="num">{money(m.revenue)}</td>
                  <td className="num">{money(m.expenses)}</td>
                  <td className="num">
                    <strong style={{ color: m.profit >= 0 ? 'var(--ok)' : 'var(--danger)' }}>{money(m.profit)}</strong>
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#fafbfc' }}>
                <td><strong>Total</strong></td>
                <td className="num"><strong>{money(d.totals.revenue)}</strong></td>
                <td className="num"><strong>{money(d.totals.expenses)}</strong></td>
                <td className="num"><strong>{money(d.totals.profit)}</strong></td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      <div style={{ height: 14 }} />

      <Card title="Expenses by category" tight>
        <table className="tbl">
          <thead>
            <tr>
              <th>Category</th>
              <th className="r">Count</th>
              <th className="r">Cost (excl. GST)</th>
              <th className="r">Total paid</th>
            </tr>
          </thead>
          <tbody>
            {d.byCategory.map((c, i) => (
              <tr key={i}>
                <td>{c.category}</td>
                <td className="num muted">{c.count}</td>
                <td className="num">{money(c.taxable)}</td>
                <td className="num">{money(c.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  )
}

// --------------------------------------------------------- input tax credit

function Itc() {
  const [month, setMonth] = useState(today().slice(0, 7))
  const [d, setD] = useState<null | {
    month: string
    rows: Record<string, string | number | null>[]
    claimable: { cgst: number; sgst: number; igst: number; cess: number; blocked: number; bills: number }
    output: { cgst: number; sgst: number; igst: number; cess: number }
    netPayable: { cgst: number; sgst: number; igst: number; total: number }
  }>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setD(null)
    api.get<never>(`/reports/itc?month=${month}`).then((x) => setD(x as never)).catch((e) => setError(e.message))
  }, [month])

  return (
    <>
      <Alert kind="error">{error}</Alert>
      <Alert kind="info">
        This is the detail behind <strong>GSTR-3B Table 4</strong>. Blocked entries are listed with their reason, so
        the gap between tax paid and credit claimed is explainable.
      </Alert>

      <div style={{ marginBottom: 14, maxWidth: 220 }}>
        <label className="small muted">Return period</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>

      {!d ? (
        <Spinner />
      ) : (
        <>
          <div className="grid c3" style={{ marginBottom: 16 }}>
            <Stat
              label="Credit claimable"
              value={money(d.claimable.cgst + d.claimable.sgst + d.claimable.igst + d.claimable.cess)}
              note={`${d.claimable.bills} GST bill${d.claimable.bills === 1 ? '' : 's'}`}
              tone="ok"
            />
            <Stat
              label="Output tax collected"
              value={money(d.output.cgst + d.output.sgst + d.output.igst + d.output.cess)}
            />
            <Stat
              label="Net payable this month"
              value={money(d.netPayable.total)}
              note={d.claimable.blocked > 0 ? `${money(d.claimable.blocked)} blocked under s.17(5)` : undefined}
            />
          </div>

          <Card title="Purchase bills with GST" tight>
            {d.rows.length === 0 ? (
              <Empty title={`No GST bills recorded for ${month}`} hint="Only expenses ticked as a GST bill appear here." />
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>GSTIN</th>
                      <th>Bill no</th>
                      <th className="r">Taxable</th>
                      <th className="r">CGST</th>
                      <th className="r">SGST</th>
                      <th className="r">IGST</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.map((r, i) => (
                      <tr key={i}>
                        <td className="nowrap small">{fmtDate(String(r.expense_date))}</td>
                        <td>{String(r.vendor_name ?? '—')}</td>
                        <td className="mono small">{String(r.vendor_gstin ?? '—')}</td>
                        <td className="mono small">{String(r.supplier_invoice_no ?? '—')}</td>
                        <td className="num">{money(Number(r.taxable_value))}</td>
                        <td className="num">{money(Number(r.cgst))}</td>
                        <td className="num">{money(Number(r.sgst))}</td>
                        <td className="num">{money(Number(r.igst))}</td>
                        <td>
                          {r.itc_eligible ? (
                            <Badge status="paid">Claim</Badge>
                          ) : (
                            <span title={String(r.itc_blocked_reason ?? '')}>
                              <Badge status="draft">Blocked</Badge>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  )
}

// ----------------------------------------------------------------------- TDS

function Tds() {
  const [d, setD] = useState<null | {
    fy: string
    bySection: Array<{ tds_section: string | null; amount: number; payments: number; gross: number }>
    byVendor: Array<{ vendor_name: string; vendor_pan: string | null; tds_section: string | null; gross: number; tds: number; payments: number }>
    byQuarter: Array<{ quarter: string; amount: number; payments: number }>
  }>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<never>('/reports/tds').then((x) => setD(x as never)).catch((e) => setError(e.message))
  }, [])

  if (error) return <Alert kind="error">{error}</Alert>
  if (!d) return <Spinner />

  const total = d.bySection.reduce((a, s) => a + s.amount, 0)

  return (
    <>
      <Alert kind="info">
        TDS you withheld from vendors in FY {d.fy}. This is money you owe the government, not the vendor — due by the
        7th of the following month, with the return each quarter.
      </Alert>

      {total === 0 ? (
        <Card>
          <Empty title="No TDS withheld this year" hint="Add a TDS amount on an expense when you deduct from a vendor payment." />
        </Card>
      ) : (
        <>
          <div className="grid c2" style={{ marginBottom: 16 }}>
            <Card title="By quarter" tight>
              <table className="tbl">
                <tbody>
                  {d.byQuarter.map((q) => (
                    <tr key={q.quarter}>
                      <td>{q.quarter}</td>
                      <td className="num muted small">{q.payments} payment{q.payments === 1 ? '' : 's'}</td>
                      <td className="num"><strong>{money(q.amount)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <Card title="By section" tight>
              <table className="tbl">
                <tbody>
                  {d.bySection.map((s, i) => (
                    <tr key={i}>
                      <td className="mono">{s.tds_section ?? '—'}</td>
                      <td className="num muted small">on {money(s.gross)}</td>
                      <td className="num"><strong>{money(s.amount)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <Card title="By vendor" tight>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>PAN</th>
                  <th>Section</th>
                  <th className="r">Gross paid</th>
                  <th className="r">TDS withheld</th>
                </tr>
              </thead>
              <tbody>
                {d.byVendor.map((v, i) => (
                  <tr key={i}>
                    <td>{v.vendor_name}</td>
                    <td className="mono small">{v.vendor_pan ?? <span className="muted">missing</span>}</td>
                    <td className="mono small">{v.tds_section ?? '—'}</td>
                    <td className="num">{money(v.gross)}</td>
                    <td className="num"><strong>{money(v.tds)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="hint" style={{ marginTop: 8 }}>
              A vendor PAN is required to file the TDS return — add it on the expense where it shows as missing.
            </div>
          </Card>
        </>
      )}
    </>
  )
}
